import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, FunnelSimple, Copy } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function History() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [connectionId, setConnectionId] = useState("all");
  const [connections, setConnections] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (status !== "all") params.status = status;
      if (connectionId !== "all") params.connection_id = connectionId;
      const { data } = await api.get("/history", { params });
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/connections").then((r) => setConnections(r.data)).catch((e) => console.error("connections load failed", e));
  }, []);
  useEffect(() => { load(); }, [status, connectionId]);

  const retry = async (r) => {
    toast.loading("Retrying…", { id: r.entry_id });
    try {
      await api.post(`/history/${r.entry_id}/retry`);
      toast.success("Retried", { id: r.entry_id });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed", { id: r.entry_id });
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-[1400px]">
      <header className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-2">History</div>
          <h1 className="font-display text-5xl font-black tracking-tight" data-testid="history-title">Request log</h1>
        </div>
      </header>

      <div className="flex items-center gap-3 mb-4">
        <div className="inline-flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-widest">
          <FunnelSimple weight="bold" size={14} /> Filter
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="filter-status" className="w-44 h-10 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="success">Success</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="skipped">Skipped</SelectItem>
          </SelectContent>
        </Select>
        <Select value={connectionId} onValueChange={setConnectionId}>
          <SelectTrigger data-testid="filter-connection" className="w-60 h-10 bg-white"><SelectValue placeholder="Connection" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All connections</SelectItem>
            {connections.map((c) => (
              <SelectItem key={c.connection_id} value={c.connection_id}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} data-testid="history-refresh" className="h-10 gap-2 ml-auto"><ArrowsClockwise weight="bold" size={14} /> Refresh</Button>
      </div>

      <div className="bg-white border border-stone-200">
        {loading ? (
          <div className="p-10 text-neutral-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-16 text-center text-neutral-500 text-sm">No requests match these filters.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-neutral-500 border-b border-stone-200">
              <tr>
                <th className="text-left py-3 px-6">When</th>
                <th className="text-left py-3 px-3">Connection</th>
                <th className="text-left py-3 px-3">Recipient</th>
                <th className="text-left py-3 px-3">HTTP</th>
                <th className="text-left py-3 px-3">Status</th>
                <th className="text-right py-3 px-6">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="history-body">
              {rows.map((r) => (
                <tr key={r.entry_id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50 cursor-pointer" onClick={() => setSelected(r)}>
                  <td className="py-3 px-6 font-mono-tech text-xs text-neutral-600">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="py-3 px-3 font-medium">{r.connection_label}</td>
                  <td className="py-3 px-3">
                    <div className="font-medium">{r.recipient_name || "—"}</div>
                    <div className="text-xs text-neutral-500 font-mono-tech">{r.recipient_email || r.recipient_phone}</div>
                  </td>
                  <td className="py-3 px-3 font-mono-tech text-xs">{r.http_status ?? "—"}</td>
                  <td className="py-3 px-3"><StatusBadge status={r.status} /></td>
                  <td className="py-3 px-6 text-right">
                    {r.status === "failed" && (
                      <button
                        data-testid={`retry-${r.entry_id}`}
                        onClick={(e) => { e.stopPropagation(); retry(r); }}
                        className="text-xs font-semibold text-[#F58021] hover:underline"
                      >
                        Retry
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-[560px] sm:max-w-[640px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-display tracking-tight">Request details</SheetTitle>
            <SheetDescription>
              {selected && (
                <span className="font-mono-tech text-xs">{selected.entry_id}</span>
              )}
            </SheetDescription>
          </SheetHeader>
          {selected && (
            <div className="mt-6 space-y-5">
              <KV label="Connection" value={selected.connection_label} />
              <KV label="Row" value={`#${selected.row_index}`} />
              <KV label="Recipient" value={`${selected.recipient_name || "—"} · ${selected.recipient_email || selected.recipient_phone || "—"}`} />
              <KV label="Status" value={<StatusBadge status={selected.status} />} />
              <KV label="HTTP" value={selected.http_status ?? "—"} />
              {selected.error && <KV label="Error" value={<span className="text-red-700">{selected.error}</span>} />}
              <JsonBlock title="Request payload" data={selected.request_payload} />
              <JsonBlock title="Response body" data={selected.response_body} />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-stone-100 pb-3">
      <div className="text-xs uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="text-sm text-neutral-900 text-right">{value}</div>
    </div>
  );
}

function JsonBlock({ title, data }) {
  const text = JSON.stringify(data ?? null, null, 2);
  const copy = () => { navigator.clipboard.writeText(text); toast.success("Copied"); };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-widest text-neutral-500">{title}</div>
        <button onClick={copy} className="text-xs text-neutral-500 hover:text-black inline-flex items-center gap-1"><Copy size={12} /> Copy</button>
      </div>
      <pre className="bg-[#0A0A0A] text-neutral-100 text-xs font-mono-tech p-4 overflow-x-auto max-h-72 rounded-sm">{text}</pre>
    </div>
  );
}
